import { CheckIcon, EyeOffIcon } from "@heroicons/react/outline";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { styled } from "~/config/stitches";
import { useUserNotificationsHook } from "~/config/user/user-notifications/UserNotifications";
import type { UserNotification } from "~/config/user/user-notifications/utils/types";
import { Button } from "~/design-system/Button";
import { ExtraButton } from "~/design-system/ButtonGroup";
import { Column, Columns } from "~/design-system/Columns";
import { MenuItem } from "~/design-system/DropdownMenu";
import { useBreakpoint } from "~/design-system/hooks";
import { createComponentHook } from "~/types";

type MultipleUserNotificationsActionMenuProps = {
  data: UserNotification[];
};

const useMultipleUserNotificationsActionMenu = createComponentHook(
  (props: MultipleUserNotificationsActionMenuProps) => {
    const { t } = useTranslation();
    const { breakpoint } = useBreakpoint();

    const { actions } = useUserNotificationsHook();
    const handleReadSelectedNotifications = () => {
      const notificationsIds = props.data.map((n) => n.id);
      void actions.handleMarkNotificationsAsRead(notificationsIds);
    };
    const handleUnreadSelectedNotifications = () => {
      const notificationsIds = props.data.map((n) => n.id);
      void actions.handleMarkNotificationsAsUnread(notificationsIds);
    };

    return {
      actions: {
        handleReadSelectedNotifications,
        handleUnreadSelectedNotifications,
      },
      state: {
        breakpoint,
      },
      t,
    };
  }
);

const StyledButton = styled(Button, {
  backgroundColor: "$white",
});

const StyledExtraButton = styled(ExtraButton, {
  backgroundColor: "$white",
});

function MultipleUserNotificationsActionMenu(
  props: MultipleUserNotificationsActionMenuProps
) {
  const { actions, state, t } = useMultipleUserNotificationsActionMenu(props);
  const markAsReadLabel = t(
    "screens.UserNotificationsScreen.MultipleUserNotificationsActionMenu.markAsRead",
    "Mark as read"
  );
  const markAsUnreadLabel = t(
    "screens.UserNotificationsScreen.MultipleUserNotificationsActionMenu.markAsUnread",
    "Mark as unread"
  );

  if (state.breakpoint === "mobile") {
    return (
      <StyledExtraButton
        variant="ghost"
        iconLabel={`selected notifications extra-actions`}
        dataClovisActionId="notifications-actions"
      >
        <MenuItem
          onClick={actions.handleReadSelectedNotifications}
          icon={<CheckIcon />}
          dataClovisActionId="notifications-mark-as-read"
        >
          {markAsReadLabel}
        </MenuItem>
        <MenuItem
          onClick={actions.handleUnreadSelectedNotifications}
          icon={<EyeOffIcon />}
          dataClovisActionId="notifications-mark-as-unread"
        >
          {markAsUnreadLabel}
        </MenuItem>
      </StyledExtraButton>
    );
  }

  return (
    <Columns space="xsmall">
      <Column width="content">
        <StyledButton
          leadingIcon={<CheckIcon />}
          tone="brandAccent"
          variant="solid"
          size="small"
          width="full"
          onClick={actions.handleReadSelectedNotifications}
          dataClovisActionId="notifications-mark-as-read"
        >
          {markAsReadLabel}
        </StyledButton>
      </Column>
      <Column width="content">
        <StyledButton
          variant="ghost"
          size="small"
          width="full"
          onClick={actions.handleUnreadSelectedNotifications}
          dataClovisActionId="notifications-mark-as-unread"
        >
          {markAsUnreadLabel}
        </StyledButton>
      </Column>
    </Columns>
  );
}

export { MultipleUserNotificationsActionMenu };
